import { memo, type ComponentPropsWithoutRef } from "react";
import type { Hint } from "~/features/chat/types";
import { useTermHighlighting } from "~/hooks/useTermHighlighting";
import type { TermMatcher } from "~/utils/termMatcher";
import { UnifiedMarkdown } from "./UnifiedMarkdown";

type HighlightedMarkdownProps = Omit<
  ComponentPropsWithoutRef<typeof UnifiedMarkdown>,
  "children"
> & {
  children: string;
  terms: Hint[];
  medicationMatcher?: TermMatcher | null;
};

/**
 * Renders markdown with medical/AI term highlighting.
 * Wraps UnifiedMarkdown, injecting <mark> tags for matched terms
 * before passing content to the markdown renderer.
 *
 * Supports two term sources:
 * - terms (AI-extracted): matched via regex
 * - medicationMatcher (Aho-Corasick): matched in O(n) for large dictionaries
 *
 * Terms are highlighted with dotted underlines; click definitions
 * are handled by a parent-level TermDefinitionDialog via event delegation.
 */
export const HighlightedMarkdown = memo(
  ({
    children,
    terms,
    medicationMatcher,
    ...rest
  }: HighlightedMarkdownProps) => {
    const highlighted = useTermHighlighting(children, terms, medicationMatcher);

    return <UnifiedMarkdown {...rest}>{highlighted}</UnifiedMarkdown>;
  }
);

HighlightedMarkdown.displayName = "HighlightedMarkdown";
